'use client';

import { memo, useState } from 'react';
import {
  Control,
  UseFormRegister,
  UseFormSetValue,
  useFormState,
  UseFormTrigger,
} from 'react-hook-form';
import NextButton from '@/components/Button/NextButton';
import Input from '@/components/Input/Input';
import { FormData } from '@/types/form';
import { fieldRules } from '@/utils/form';
import { FormDocumentData, Simplify } from '../../../../../prismicio-types';
import TransparentLabel from '../TransparentLabel/TransparentLabel';
import { WarningIcon } from '@/assets/icons/WarningIcon';
import generalStyles from '../FormBody.module.scss';
import useModal from '@/hooks/useModal';
import { formatSSN } from '@/utils/validation';
import styles from './StepEleven.module.scss';

interface Props {
  data?: Simplify<FormDocumentData>;
  register?: UseFormRegister<FormData>;
  handleSubmit?: (e?: React.BaseSyntheticEvent) => Promise<void>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  control?: Control<FormData, any, FormData>;
  isModal?: boolean;
  trigger?: UseFormTrigger<FormData>;
  setValue?: UseFormSetValue<FormData>;
  formData?: FormData;
  stepLoading?: boolean;
  verificationError?: boolean;
}

const unformatSSN = (formattedValue: string) =>
  formattedValue.replace(/\D/g, '');

const SSNForm = ({
  handleSubmit,
  register,
  control,
  isModal = false,
  trigger,
  data,
  setValue,
  formData,
  stepLoading = false,
  verificationError = false,
}: Props) => {
  const { closeModal } = useModal();
  const { errors } = useFormState({ control });
  const [focused, setFocused] = useState(false);

  const submitForm = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const isValid = await trigger?.();
    if (isValid) {
      await handleSubmit?.();
      if (isModal) {
        closeModal();
      }
    }
  };

  const handleSSNChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target;
    const rawValue = unformatSSN(input.value);
    const cursorPosition = input.selectionStart || 0;
    const formattedValue = formatSSN(rawValue);
    const addedChars = formattedValue.length - input.value.length;
    e.target.value = formattedValue;
    setValue?.('ssn', formattedValue);

    setTimeout(() => {
      const newCursorPosition = cursorPosition + addedChars;
      input.setSelectionRange(newCursorPosition, newCursorPosition);
    });
  };

  return (
    <form className={styles.form} onSubmit={submitForm}>
      {(!isModal || focused) && register && (
        <Input
          id="ssn"
          label="Social security number"
          placeholder="###-##-####"
          inputMode="numeric"
          type="text"
          register={register('ssn', {
            ...fieldRules.ssn,
            onChange: handleSSNChange,
          })}
          error={errors?.ssn}
        />
      )}

      {isModal && !focused && (
        <div
          onClick={() => {
            setFocused(true);
            setValue?.('ssn', '');
          }}
          className={styles.fakeInputWrapper}
        >
          <p className={styles.fakeLabel}>Social security number</p>
          <div className={styles.fakeInput}>
            {formData ? `***-**-${formData.ssn.slice(-4)}` : ''}
          </div>
        </div>
      )}
      <NextButton
        buttonType="submit"
        type="button"
        text={isModal ? 'Apply changes' : 'Go to review'}
        styles={`${generalStyles.button} ${isModal ? generalStyles.smallButton : ''}`}
        loading={stepLoading}
        disabled={stepLoading}
      />
      {verificationError && (
        <p
          className={`${generalStyles.error} ${generalStyles.verificationError}`}
        >
          <WarningIcon className={generalStyles.warningIcon} />
          <span>We couldn&apos;t verify that you are human</span>
        </p>
      )}
      {data && (
        <div className={styles.labelsWrapper}>
          {data.step11_labels.map((label) => (
            <TransparentLabel
              key={label.text}
              icon={label.icon}
              text={label.text}
            />
          ))}
        </div>
      )}
    </form>
  );
};

export default memo(SSNForm);
